import React from 'react';
import { User, Lock, Save } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface UserProfile {
  name: string;
  whatsapp: string;
  created_at?: string;
  referral_code?: string;
}

export function ProfileScreen() {
  const navigate = useNavigate();
  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [passwordForm, setPasswordForm] = React.useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = React.useState(false);
  const [isChangingPassword, setIsChangingPassword] = React.useState(false);

  React.useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      // First verify the connection and session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error('Erro de sessão: ' + sessionError.message);
      }

      if (!session) {
        // If no session, redirect to login
        navigate('/login');
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        throw new Error('Erro ao buscar usuário: ' + userError.message);
      }

      if (!user) {
        // If no user, redirect to login
        navigate('/login');
        throw new Error('Usuário não encontrado');
      }

      // Get user referral code if exists - using maybeSingle() to handle no results
      const { data: referralData, error: referralError } = await supabase
        .from('user_referrals')
        .select('referral_code')
        .eq('referred_user_id', user.id)
        .maybeSingle();

      if (referralError) {
        console.error('Error fetching referral:', referralError);
      }

      const metadata = user.user_metadata;
      const userProfile = {
        name: metadata?.name || '',
        whatsapp: (metadata?.whatsapp || '').replace('+55', ''),
        created_at: user.created_at,
        referral_code: referralData?.referral_code
      };

      setProfile(userProfile);
      setError(null);
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar perfil');
      
      // If the error is related to authentication, redirect to login
      if (err instanceof Error && 
          (err.message.includes('Sessão expirada') || 
           err.message.includes('Usuário não encontrado'))) {
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    // Validate passwords
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('As senhas não conferem');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }

    try {
      // First verify current password by trying to sign in
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        navigate('/login');
        throw new Error('Usuário não encontrado');
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordForm.currentPassword,
      });

      if (signInError) {
        setPasswordError('Senha atual incorreta');
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword,
      });

      if (updateError) throw updateError;

      setPasswordSuccess(true);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setIsChangingPassword(false);
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError(err instanceof Error ? err.message : 'Erro ao atualizar senha');
      
      // If the error is related to authentication, redirect to login
      if (err instanceof Error && err.message.includes('Usuário não encontrado')) {
        navigate('/login');
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="text-center py-4">Carregando...</div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <User className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-900">
            Meu Perfil
          </h2>
        </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Nome completo
              </label>
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profile?.name}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                WhatsApp
              </label>
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profile?.whatsapp}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Data de cadastro
              </label>
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{formatDate(profile?.created_at)}</div>
            </div>

            {profile?.referral_code && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Cupom utilizado
                </label>
                <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profile.referral_code}</div>
              </div>
            )}
          </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-900">
            Alterar Senha
          </h2>
        </div>

        {passwordError && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {passwordError}
          </div>
        )}

        {passwordSuccess && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
            Senha alterada com sucesso!
          </div>
        )}

        {isChangingPassword ? (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Senha atual
              </label>
              <input
                required
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nova senha
              </label>
              <input
                required
                type="password"
                minLength={6}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmar nova senha
              </label>
              <input
                required
                type="password"
                minLength={6}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                  });
                  setPasswordError(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Save size={20} />
                Salvar nova senha
              </button>
            </div>
          </form>
        ) : (
          <div>
            <button
              onClick={() => setIsChangingPassword(true)}
              className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              Alterar senha
            </button>
          </div>
        )}
      </div>
    </div>
  );
}